import { CHUNKS_PER_PART } from "@reflecto/core";

/** One global part's placement: which file it belongs to and the file-local chunk range it covers. */
export interface PartPlanEntry {
  /** 0-based global part index = the R2 object number (`ft/{tid}/{globalPart}`). */
  readonly globalPart: number;
  /** Index into the `files` array passed to `partPlan`. */
  readonly fileIndex: number;
  readonly fileId: string;
  /** First chunk index WITHIN the file (0-based). */
  readonly localChunkStart: number;
  /** Last chunk index WITHIN the file (inclusive). */
  readonly localChunkEndInclusive: number;
}

/**
 * Map a multi-file transfer's global part indices to per-file chunk ranges,
 * FILE-ALIGNED: every part belongs to exactly one file (a part never straddles a
 * file boundary), so each file's last part may be short. This keeps per-file
 * hash / AEAD AAD / save-offset math clean at the cost of a fractional wasted R2
 * object per file (docs/superpowers/specs/2026-07-26-multi-file-transfer-design.md §4).
 *
 * `partPlan(files).length === Σ ceil(fileₙ.chunkCount / CHUNKS_PER_PART)`; the
 * returned array is indexed by `globalPart` (entry `i` has `globalPart === i`).
 * The degenerate single-file case equals today's `ceil(chunkCount / CHUNKS_PER_PART)`.
 *
 * Precondition: every `chunkCount ≥ 1` (a 0-byte file is one empty final chunk —
 * the `FileEntry` contract, enforced by `validateManifest`). If it is ever
 * violated, this throws rather than silently dropping the file from the plan
 * (`Math.ceil(0 / CHUNKS_PER_PART) === 0` would contribute zero parts — a silent
 * data-loss failure mode). Fail loud instead. The message carries only the
 * numeric `fileIndex`, never file content.
 */
export function partPlan(
  files: readonly { readonly fileId: string; readonly chunkCount: number }[]
): PartPlanEntry[] {
  const plan: PartPlanEntry[] = [];
  let globalPart = 0;
  files.forEach((file, fileIndex) => {
    if (file.chunkCount < 1) {
      throw new Error(`partPlan: chunkCount must be >= 1 (fileIndex ${fileIndex})`);
    }
    const nParts = Math.ceil(file.chunkCount / CHUNKS_PER_PART);
    for (let localPart = 0; localPart < nParts; localPart += 1) {
      const localChunkStart = localPart * CHUNKS_PER_PART;
      const localChunkEndInclusive = Math.min(
        localChunkStart + CHUNKS_PER_PART - 1,
        file.chunkCount - 1
      );
      plan.push({
        globalPart,
        fileIndex,
        fileId: file.fileId,
        localChunkStart,
        localChunkEndInclusive,
      });
      globalPart += 1;
    }
  });
  return plan;
}

/**
 * Each file's GLOBAL chunk offset — the cumulative `chunkCount` of every EARLIER
 * file, indexed by `fileIndex` (so `offsets[i] + localChunkIndex` is that chunk's
 * file-aligned GLOBAL chunk index, in the same manifest order `partPlan` walks).
 *
 * THE single definition of that offset for the whole engine, deliberately: it is
 * both the receiver's whole-transfer `chunksVerified` progress cursor AND —
 * since the multi-file nonce fix — the AEAD nonce counter (crypto-spec.md §6a).
 * A per-file nonce counter would reuse a (key, nonce) pair across files, because
 * a transfer has exactly one key and one `noncePrefix`; the AAD's `fileId` only
 * binds the Poly1305 tag, not the keystream. Both sides derive this from the
 * manifest alone, so it never travels on the wire.
 *
 * `offsets[0]` is always 0, so a single-file transfer's global chunk index equals
 * its local one and every byte matches the pre-multi-file behaviour.
 */
export function globalChunkOffsets(chunkCounts: readonly number[]): number[] {
  const offsets: number[] = [];
  let cumulative = 0;
  for (const count of chunkCounts) {
    offsets.push(cumulative);
    cumulative += count;
  }
  return offsets;
}
